// React Query hooks for planner structure operations
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { PlannerStructure, StructureItem, SceneContent } from "@/types/database";
import { useRepository } from "@/contexts/PlatformContext";
import type { DataRepository } from "@/types/repository";
import { isWebCompanionPlatform } from "@/utils/platform";
import { androidRepository } from "@/lib/repository/androidRepository";
import { webRepository } from "@/lib/repository/webRepository";

// Query keys
export const plannerKeys = {
  structure: (manuscriptId: string) => ["planner", manuscriptId] as const,
};

// Factory: create a fresh default structure with unique IDs per call
export function createDefaultStructure(): StructureItem[] {
  return [
    {
      id: crypto.randomUUID(),
      title: "Act One",
      type: "act",
      children: [
        {
          id: crypto.randomUUID(),
          type: "chapter",
          children: [
            {
              id: crypto.randomUUID(),
              type: "scene",
              metadata: { status: "draft" },
            },
          ],
        },
      ],
    },
  ];
}

/** Find the first scene ID in a structure */
export function findFirstSceneId(structure: StructureItem[]): string | null {
  for (const act of structure) {
    for (const chapter of act.children ?? []) {
      if (chapter.children?.[0]) {
        return chapter.children[0].id;
      }
    }
  }
  return null;
}

/**
 * Create a planner structure and its initial empty scene content in one shot.
 * Call this at manuscript creation time so IDs are stable before the editor opens.
 */
export async function createInitialPlannerAndScene(
  manuscriptId: string,
  repository?: DataRepository
): Promise<{ structure: PlannerStructure; firstSceneId: string }> {
  const targetRepo = repository ?? (isWebCompanionPlatform() ? webRepository : androidRepository);
  const items = createDefaultStructure();
  const structure: PlannerStructure = { id: manuscriptId, structure: items };

  await targetRepo.putPlannerStructure(structure);

  const firstSceneId = findFirstSceneId(items)!;
  const emptyScene: SceneContent = { id: firstSceneId, manuscriptId, content: "" };

  await targetRepo.putSceneContent(emptyScene);

  return { structure, firstSceneId };
}

// READ-ONLY hook — does NOT create structure if missing
export function usePlannerStructure(manuscriptId: string | null) {
  const repository = useRepository();
  return useQuery({
    queryKey: plannerKeys.structure(manuscriptId ?? ""),
    queryFn: async () => {
      if (!manuscriptId) return undefined;
      return await repository.getPlannerStructure(manuscriptId);
    },
    enabled: !!manuscriptId,
    staleTime: 1000 * 60 * 5,
  });
}

export function useUpdatePlannerStructure() {
  const queryClient = useQueryClient();
  const repository = useRepository();

  return useMutation({
    mutationFn: (plannerStructure: PlannerStructure) => repository.putPlannerStructure(plannerStructure),
    onSuccess: (_, plannerStructure) => {
      queryClient.invalidateQueries({ queryKey: plannerKeys.structure(plannerStructure.id) });
    },
  });
}

// Helper to create new IDs
export function generateId(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

// Deep clone helper for immutable updates
export function cloneStructure(structure: StructureItem[]): StructureItem[] {
  return JSON.parse(JSON.stringify(structure));
}
